import {getTempUserToken, getUserToken} from "@/services/storage-data";
import {
  createAndUploadFilePreview,
  createCloudFilesUploadSession,
  isDirectGcsUploadFilesApi,
  validateFileForUpload,
} from "@/widgets/file-uploader-widget/file-api-utils";
import { useRecoilState } from "recoil";
import { printHouseProfile } from "@/store/print-house-profile";
import {
  pinFileUploaderState,
  uploadingFilesState,
  fileUploaderTargetSkuState,
} from "@/store/file-uploader-state";
import {
  EUploadingFileStatus,
  FileTypeENUM,
} from "@/widgets/file-uploader-widget/interface";
import { boardMissionsDetailsState } from "@/widgets/production-floor/state/boards";
import { useGomakeAxios } from "@/hooks";
import { useFileUploader } from "@/providers/signalr-provider/file-uploader-provider";
import { EPreferences } from "@/enums/prefernces";
import { usePrintHousePreferences } from "@/hooks/use-print-house-preferences";
import {useEffect, useState} from "react";
import axios from "axios";
import { useSnackBar } from "@/hooks/use-snack-bar";
import { setOrderItemFileSkuApi } from "@/services/api-service/production-floor/production-floor-endpoints";

const resolveUploadPath = (
  fileType: FileTypeENUM,
  filesPath: string,
  graphicsFilePath: string,
  miscellaneousFilePath?: string,
) => {
  switch (fileType) {
    case FileTypeENUM.Printing:
      return filesPath;
    case FileTypeENUM.Miscellaneous:
      return miscellaneousFilePath ?? filesPath;
    default:
      return graphicsFilePath;
  }
};

const normalizeUploadSkus = (
  skus?: string[],
  targetSku?: string | null
): string[] => {
  const fromArg = (skus || [])
    .map((value) => (typeof value === "string" ? value.trim() : ""))
    .filter(Boolean);
  if (fromArg.length > 0) {
    return fromArg;
  }
  const fromTarget = targetSku?.trim();
  return fromTarget ? [fromTarget] : [];
};

const useUploadFiles = (
  orderItemId, 
  filesPath, 
  graphicsFilePath,
  isComplexProduct?: boolean,
  productType?: string,
  isOpenPrintingModal?: any,
  miscellaneousFilePath?: string,
) => {
  const CHUNK_SIZE = 10 * 1024 * 1024; // 5MB
  const [companyProfile] = useRecoilState(printHouseProfile);
  const { activateFileUploaderSession } = useFileUploader();
  const { alertFault } = useSnackBar();
  const [, setShowFileUploader] = useRecoilState(pinFileUploaderState);
  const [uploadingFiles, setUploadingFiles] = useRecoilState(uploadingFilesState);
  const [targetSku] = useRecoilState(fileUploaderTargetSkuState);
  const [boardMissions] = useRecoilState(boardMissionsDetailsState);
  const { CheckPreferenceValue } = usePrintHousePreferences();
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [finalFilePath, setFinalFilePath] = useState("");
  const { callApi } = useGomakeAxios();
  useEffect(()=>{
    // Check if we should open printing modal
    const finalFilesGroup = uploadingFiles?.find(
        (fg) => fg.orderItemId === orderItemId && fg.productType === productType
    );

    if (CheckPreferenceValue(EPreferences.USE_PRINTNODE_AFTER_UPLOAD_FILE) && isFileUploaded && finalFilesGroup
        && finalFilesGroup.filesInfo.length == 1 && finalFilesGroup.filesInfo[0].fileStatus === EUploadingFileStatus.Uploaded) {
      isOpenPrintingModal?.open();
      setIsFileUploaded(false)
    }
  },[uploadingFiles,isFileUploaded])

  const linkUploadedFileSkus = async (fileId: string, skus: string[]) => {
    if (!fileId || skus.length === 0) {
      return;
    }
    await setOrderItemFileSkuApi(callApi, () => undefined, [
      {
        id: fileId,
        sku: skus[0],
        skus,
      },
    ]);
  };

  const handleFileUpload = async (
    file,
    fileType,
    skus?: string[]
  ) => {
    const validationError = validateFileForUpload(file);
    if (validationError) {
      alertFault(validationError);
      return;
    }

    const resolvedSkus = normalizeUploadSkus(skus, targetSku);
    const primarySku = resolvedSkus[0] || undefined;

    const screenshotPromise = createAndUploadFilePreview(
      companyProfile.filesApiAddress,
      file,
    );

    const activeConnectionId = await activateFileUploaderSession();
    const finalPath = resolveUploadPath(
      fileType,
      filesPath,
      graphicsFilePath,
      miscellaneousFilePath,
    );
    setShowFileUploader(true);

    // Generate a unique identifier for this file upload
    const uploadId = `${orderItemId}_${file.name}_${Date.now()}`;

    // Update files state to show uploading status
    const currentFilesGroup = uploadingFiles?.find(
      (fg) => fg.orderItemId === orderItemId && fg.productType === productType
    );

    if (currentFilesGroup) {
      setUploadingFiles(prev =>
        prev.map((fg) =>
          fg.orderItemId === orderItemId && fg.productType === productType
            ? {
                ...fg,
                filesInfo: [
                  {
                    fileName: file?.name,
                    fileStatus: EUploadingFileStatus.UPLOADING,
                    fileType,
                    uploadId, // Add uploadId to track this specific upload
                  },
                  ...fg.filesInfo,
                ],
              }
            : fg
        )
      );
    } else {
      setUploadingFiles(prev => [
        {
          orderItemId,
          boardMissionId: boardMissions.boardMissionId,
          filePath: filesPath,
          graphicsFilePath: graphicsFilePath,
          miscellaneousFilePath: miscellaneousFilePath,
          productType: productType,
          title: `${boardMissions.boardMissionNumber} | ${boardMissions.orderNumber} | ${boardMissions.clientName}`,
          hotFolderName: file?.hotFolderName,
          filesInfo: [
            {
              fileName: file?.name,
              fileStatus: EUploadingFileStatus.UPLOADING,
              fileType,
              uploadId, // Add uploadId to track this specific upload
            },
          ],
        },
        ...prev,
      ]);
    }

    // Initial status update to get fileId
    const initialStatusResponse = await setOrderItemFileStatus(
      {
        orderItemId,
        filename: file?.name,
        source: finalPath,
        productType: !!productType ? productType.toString() : "",
        signalRConnectId: activeConnectionId,
        status: EUploadingFileStatus.UPLOADING,
        fileType,
      },
      resolvedSkus
    );

    // Get the fileId specific to this upload
    const fileId = initialStatusResponse?.data?.data?.data;

    if(isDirectGcsUploadFilesApi(companyProfile.filesApiAddress)){
      try {
        const { sessionUri, bucketName } = await createCloudFilesUploadSession(
          companyProfile.filesApiAddress,
          file,
        );
        await uploadFileToGCS(sessionUri, file, (progress) => {});
        const response2 = await axios.post(
            `${companyProfile.filesApiAddress}/api/Files/SendFileToPrintHouseFileApp`,
            {
              orderItemId:orderItemId,
              fileName: file?.name,
              fileType: fileType,
              productType: productType,
              bucketKey:bucketName,
              ...(primarySku ? { sku: primarySku } : {}),
              ...(resolvedSkus.length > 0 ? { skus: resolvedSkus } : {}),
            },
            {
              headers: {
                authorization: "Bearer " + (getUserToken() || getTempUserToken()),
              },
            }
        );
      const finalFilePath = response2.data.filePath;
      setFinalFilePath(finalFilePath);
      const screenshotUrl = await screenshotPromise;
      setUploadingFiles(prev =>
          prev.map((fg) =>
              fg.orderItemId === orderItemId && fg.productType === productType
                  ? {
                    ...fg,
                    filesInfo: fg.filesInfo.map(fileInfo =>
                        fileInfo.uploadId === uploadId
                            ? { ...fileInfo, fileStatus: EUploadingFileStatus.UploadedToGoogleCloud,bucketKey:bucketName,downloadFilePath:finalFilePath }
                            : fileInfo
                    ),
                  }
                  : fg
          )
      );

        await setOrderItemFileStatus(
          {
            orderItemId,
            filename: file?.name,
            source: finalFilePath,
            screenShot: screenshotUrl || "",
            productType: !!productType ? productType.toString() : "",
            signalRConnectId: activeConnectionId,
            status: EUploadingFileStatus.UploadedToGoogleCloud,
            fileType,
            fileId: fileId,
            bucketKey: bucketName,
          },
          resolvedSkus
        );
        if (fileId) {
          await linkUploadedFileSkus(fileId, resolvedSkus);
        }
      return fileId;
      } catch (error) {
        const message =
          error instanceof Error
            ? error.message
            : "Failed to start file upload session.";
        alertFault(message);
        return;
      }
    }
    // File upload logic
    const totalChunks = Math.ceil(file.size / CHUNK_SIZE);
    let currentChunk = 0;
    let fileNameToUpload = file?.name;
    while (currentChunk < totalChunks) {
      const start = currentChunk * CHUNK_SIZE;
      const end = Math.min(start + CHUNK_SIZE, file.size);
      const chunk = file.slice(start, end);
      
      // Get the latest state for hotFolderName
      const latestFilesGroup = uploadingFiles?.find(
        (fg) => fg.orderItemId === orderItemId && fg.productType === productType
      );
      const hotFolderName = latestFilesGroup?.hotFolderName;

      let response = await uploadChunk(
        chunk,
        currentChunk,
        totalChunks, 
        fileNameToUpload,
        fileType,
        productType,
        hotFolderName,
        fileId, // Pass the specific fileId for this upload
        uploadId, // Pass uploadId to identify this specific upload
        activeConnectionId
      );

      if (!response || !response.success) {
        // Update error status for this specific file
        setUploadingFiles(prev =>
          prev.map((fg) =>
            fg.orderItemId === orderItemId && fg.productType === productType
              ? {
                  ...fg,
                  filesInfo: fg.filesInfo.map(fileInfo =>
                    fileInfo.uploadId === uploadId
                      ? { ...fileInfo, fileStatus: EUploadingFileStatus.ERROR }
                      : fileInfo
                  ),
                }
              : fg
          )
        );
        
        await setOrderItemFileStatus(
          {
            orderItemId,
            filename: file?.name,
            source: finalPath,
            screenShot: "",
            productType: !!productType ? productType.toString() : "",
            signalRConnectId: activeConnectionId,
            status: EUploadingFileStatus.ERROR,
            fileType,
            fileId: fileId
          },
          resolvedSkus
        );
        return;
      }
      fileNameToUpload = response.filename;
      currentChunk++;
      
      if (currentChunk === totalChunks) {
        setIsFileUploaded(true)
        const screenshotUrl = await screenshotPromise;
        // Update success status for this specific file
        setUploadingFiles(prev =>
          prev.map((fg) =>
            fg.orderItemId === orderItemId && fg.productType === productType
              ? {
                  ...fg,
                  filesInfo: fg.filesInfo.map(fileInfo =>
                    fileInfo.uploadId === uploadId
                      ? { ...fileInfo, fileStatus: response.isUploadedToGoogleCloud ? EUploadingFileStatus.UploadedToGoogleCloud : EUploadingFileStatus.Uploaded,downloadFilePath:response.source ,bucketKey:response.bucketKey}
                      : fileInfo
                  ),
                }
              : fg
          )
        );
        
        await setOrderItemFileStatus(
          {
            orderItemId,
            filename: file?.name,
            source: response.source,
            screenShot: screenshotUrl || response.screenShot,
            productType: !!productType ? productType.toString() : "",
            signalRConnectId: activeConnectionId,
            status: response.isUploadedToGoogleCloud ? EUploadingFileStatus.UploadedToGoogleCloud : EUploadingFileStatus.Uploaded,
            fileType,
            fileId: fileId,
            bucketKey: response.bucketKey,
          },
          resolvedSkus
        );
        if (fileId) {
          await linkUploadedFileSkus(fileId, resolvedSkus);
        }
        if(response.jdfFileUrl){
          await setOrderItemFileStatus(
            {
              orderItemId,
              filename: response.jdfFileName,
              source: response.jdfFileUrl,
              screenShot: "",
              productType: !!productType ? productType.toString() : "",
              signalRConnectId: activeConnectionId,
              status: EUploadingFileStatus.Uploaded,
              fileType,
              fileId: response.jdfFileId,
            },
            resolvedSkus
          );
          setUploadingFiles(prev =>
              prev.map((fg) =>
                  fg.orderItemId === orderItemId && fg.productType === productType
                      ? {
                        ...fg,
                        filesInfo: [...fg.filesInfo.map(fileInfo =>
                            fileInfo.uploadId === uploadId
                                ? { ...fileInfo, fileStatus: EUploadingFileStatus.Uploaded,downloadFilePath:response.source }
                                : fileInfo
                        ),...fg.filesInfo.map(fileInfo =>
                            fileInfo.uploadId === uploadId
                                ? { ...fileInfo,fileName:response.jdfFileName, fileStatus: EUploadingFileStatus.Uploaded,downloadFilePath:response.jdfFileUrl }
                                : fileInfo
                        ),]
                      }
                      : fg
              )
          );
        }
        
      }
    }
    return fileId;
  };
  async function uploadFileToGCS(
      sessionUri: string,
      file: File,
      onProgress?: (n: number) => void
  ) {
    const chunkSize = 8 * 1024 * 1024; // 8 MiB; tune to 8–32 MiB
    let offset = 0;

    while (offset < file.size) {
      const end = Math.min(offset + chunkSize, file.size);
      const chunk = file.slice(offset, end);
      const res = await fetch(sessionUri, {
        method: "PUT",
        headers: {
          "Content-Type": file.type || "application/octet-stream",
          "Content-Range": `bytes ${offset}-${end - 1}/${file.size}`,
        },
        body: chunk,
      });
      if (!(res.ok || res.status === 308))
        throw new Error(`Upload failed: ${res.status}`);
      offset = end;
      onProgress?.(Math.round((offset / file.size) * 100));
    }
  }
  const uploadChunk = async (
    chunk,
    chunkIndex,
    totalChunks,
    fileName,
    fileType,
    productType,
    hotFolderName,
    fileId, // Now passed as parameter
    uploadId, // For tracking
    signalRConnectId: string
  ) => {
    
    const formData = new FormData();
    formData.append("file", chunk);
    formData.append("uploadFileRequestId", uploadId);
    formData.append("productType", productType ? productType.toString() : null);
    formData.append("orderItemId", orderItemId);
    formData.append("isComplexProduct", isComplexProduct ? "true" : "false");
    formData.append("fileName", fileName);
    formData.append("hotFolderName", hotFolderName || "");
    formData.append(
      "takeScreenShot",
      chunkIndex + 1 === totalChunks ? "true" : "false"
    );
    formData.append("currentPacket", (chunkIndex + 1).toString());
    formData.append("totalPackets", totalChunks.toString());
    formData.append("signalRConnectId", signalRConnectId);
    formData.append("fileId", fileId || "");
    formData.append("fileType", fileType);
    formData.append(
      "path",
      resolveUploadPath(
        fileType,
        filesPath,
        graphicsFilePath,
        miscellaneousFilePath,
      ),
    );

    try {
      const response = await fetch(
        companyProfile.filesApiAddress + "/api/Files/UploadOrderItemFile",
        {
          method: "POST",
          body: formData,
          headers: {
            authorization: "Bearer " + getUserToken(),
          },
        }
      );

      const data = await response.json();
      return data;
    } catch (error) {
      console.error("Error uploading chunk:", error);
      return null;
    }
  };

  const setOrderItemFileStatus = async (
    dto,
    skus: string[] = []
  ) => {
    // Caller already resolved skus (including optional targetSku); do not re-merge.
    const primarySku = skus[0];
    const res: any = await callApi(
      "POST",
      `/v1/erp-service/board-missions/set-order-item-file-status`,
      {
        ...dto,
        boardMissionIds: uploadingFiles.map(
          (fileGroup) => fileGroup.boardMissionId
        ),
        fileId: dto.fileId,
        ...(primarySku ? { sku: primarySku } : {}),
        ...(skus.length > 0 ? { skus } : {}),
      },
      false
    );
    return res;
  };

  const onClickCopyPath = async () => {
    try {
      // Construct the file path - prefer downloadFilePath, then filePath, then fileName
      let pathToCopy = finalFilePath;
      
      // Prepend localFilePathMapping if it exists
      if (companyProfile?.localFilePathMapping) {
        // Ensure proper path joining - remove trailing slash from mapping and leading slash from path if present
        const mapping = companyProfile.localFilePathMapping.replace(/[/\\]+$/, '');
        const filePathPart = pathToCopy.replace(/^[/\\]+/, '');
        // Join with appropriate separator based on the mapping (Windows uses \, Unix uses /)
        const separator = mapping.includes('\\') ? '\\' : '/';
        pathToCopy = `${mapping}${separator}${filePathPart}`;
      }
      
      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        await navigator.clipboard.writeText(pathToCopy);
      } else {
        // Fallback for older browsers
        const textArea = document.createElement("textarea");
        textArea.value = pathToCopy;
        textArea.style.position = "fixed";
        textArea.style.opacity = "0";
        document.body.appendChild(textArea);
        textArea.select();
        document.execCommand("copy");
        document.body.removeChild(textArea);
      }
    } catch (error) {
      console.error("Failed to copy file path:", error);
    }
  };


  return {
    handleFileUpload,
    onClickCopyPath,
    finalFilePath
  };
};

export { useUploadFiles };
